import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'contexts/authContext';
import { Dropdown } from 'components/_new/Dropdown';
import { Button } from 'components/_new/Button';
import { OrganizationRole } from 'types/Membership';
import { Divider } from '../../_new/Divider';
import { Link } from '../../_new/Link';

type ChangeOrganizationProps = {
	card?: boolean;
	onCancel?: () => void;
	defaultSelected?: {
		organization: string;
		program: string;
	};
};

const ChangeOrganization: FC<ChangeOrganizationProps> = ({ defaultSelected, card, onCancel }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { logout, currentMemberships } = useAuth();

	const [selectedPath, setSelectedPath] = useState<{ organization: string | null; program: string | null }>(
		defaultSelected || { organization: null, program: null }
	);
	const [isProgram, setIsProgram] = useState(!!defaultSelected?.program);

	const cutData = currentMemberships?.map((membership) => {
		const cutPrograms = membership.programs?.map((program) => ({
			...program,
			name: program.name.split('/')[1],
		}));
		return { ...membership, programs: cutPrograms };
	});
	const userMemberships = cutData?.filter((membership) => membership.programs && membership.programs.length > 0);
	const hasAdminAccess = currentMemberships?.some(
		(membership) => membership.role === OrganizationRole.ORGANIZATION_ADMIN
	);

	const findOrg = (value: string | null) =>
		userMemberships?.find((membership) => membership.organization.name === value);

	const changeOrg = (value: string) => {
		setIsProgram(true);
		const selectedOrg = findOrg(value);

		setSelectedPath({
			organization: value,
			program: selectedOrg?.programs.length ? selectedOrg.programs[0].name : '',
		});
	};

	const getPrograms = () => {
		const selectedOrg = findOrg(selectedPath?.organization);
		return selectedOrg?.programs?.map(({ name, displayName }) => ({
			label: displayName,
			value: name,
		}));
	};

	if (userMemberships?.length === 0) {
		return (
			<div className="flex flex-column align-items-center">
				<p>{t('organizationPanel.noAdminAccess')}</p>
				<Button className="mt-3" onClick={() => navigate('/')} label={t('navigation.returnToHomepPage')} />
			</div>
		);
	}
	return (
		<div className="flex flex-column w-full gap-3">
			<div>
				<span>{t('landingPages.buttons.chooseOrganization')}</span>
			</div>

			<Dropdown
				value={selectedPath.organization}
				options={
					userMemberships &&
					userMemberships.map(({ organization }) => ({
						label: organization.displayName,
						value: organization.name,
					}))
				}
				placeholder={t('navigation.chooseOrganization')}
				onChange={(e) => changeOrg(e.value)}
			/>

			{isProgram && (
				<Dropdown
					value={selectedPath.program}
					options={getPrograms()}
					placeholder={t('navigation.chooseProgram')}
					onChange={(e) => setSelectedPath({ ...selectedPath, program: e.value })}
				/>
			)}

			<Button
				className="flex justify-content-center mt-2"
				onClick={() => {
					navigate({
						pathname: `/${selectedPath.organization}/${selectedPath.program}`,
					});
				}}
				disabled={
					!selectedPath.organization ||
					(isProgram &&
						selectedPath.program === defaultSelected?.program &&
						selectedPath.organization === defaultSelected?.organization)
				}
				label={t('misc.choose')}
				block
			/>

			{!card && (
				<>
					<div className="flex justify-content-center">
						<span>{t('misc.or')}</span>
					</div>
					<Button
						className="flex justify-content-center"
						onClick={() => {
							logout();
						}}
						label={t('landingPages.buttons.logout')}
						block
					/>
				</>
			)}

			{card && onCancel && (
				<>
					<div className="flex justify-content-center">
						<span>{t('misc.or')}</span>
					</div>
					<Button
						className="flex justify-content-center"
						onClick={onCancel}
						label={t('actions.cancel')}
						block
					/>
				</>
			)}

			{hasAdminAccess && (
				<div>
					<Divider />
					<Link className="p-link flex justify-content-center mt-3" to="/organization/list">
						{t('organizationPanel.adminPanel')}
					</Link>
				</div>
			)}
		</div>
	);
};

export default ChangeOrganization;
